import React from 'react';
import { useNavigate } from 'react-router-dom';

interface NavigationFooterProps {
  // Navigation
  backRoute?: string;
  nextRoute?: string;
  onBack?: () => void;
  onNext?: () => void;
  
  // Progress section
  progressTitle: string;
  stepText: string;
  progressPercentage: number;
  
  // Button states
  nextDisabled?: boolean;
  backDisabled?: boolean;
  
  // Button labels (optional customization)
  backLabel?: string;
  nextLabel?: string;
  
  // Layout variants
  variant?: 'responsive' | 'fixed';
  fixedWidth?: string;
  marginLeft?: string;
  
  // Styling overrides
  className?: string;
}

const NavigationFooter: React.FC<NavigationFooterProps> = ({
  backRoute,
  nextRoute,
  onBack,
  onNext,
  progressTitle,
  stepText,
  progressPercentage,
  nextDisabled = false,
  backDisabled = false,
  backLabel = 'Back',
  nextLabel = 'Next',
  variant = 'responsive',
  fixedWidth = '759px',
  marginLeft = '253px',
  className = ''
}) => {
  const navigate = useNavigate();

  const handleBack = () => {
    if (onBack) {
      onBack();
    } else if (backRoute) {
      navigate(backRoute);
    }
  };

  const handleNext = () => {
    if (onNext) {
      onNext();
    } else if (nextRoute) {
      navigate(nextRoute);
    }
  };

  if (variant === 'fixed') {
    return (
      <div 
        className={`mt-16 h-24 bg-cyan-900 rounded-md shadow-[0_0_16px_4px_rgba(49,49,33,0.20)] flex flex-col justify-center px-6 py-4 relative ${className}`}
        style={{ width: fixedWidth, marginLeft }}
      >
        <p className="font-sans text-xl font-medium text-stone-50 leading-relaxed">{progressTitle}</p>
        <p className="font-sans text-xs font-normal text-stone-50/50 leading-none mt-1">{stepText}</p>
        
        {/* Progress bar */}
        <div className="mt-3 w-[728px] h-1.5 bg-stone-400 rounded-md relative">
          <div 
            className="absolute top-0 left-0 h-1.5 bg-green-900 rounded-md transition-all duration-300"
            style={{ width: `${Math.max(0, Math.min(100, progressPercentage))}%` }}
          />
        </div>

        {/* Back button */}
        <button
          onClick={handleBack}
          disabled={backDisabled}
          className={`absolute left-6 top-1/2 -translate-y-1/2 font-sans text-base text-stone-50 inline-flex items-center gap-1 transition-opacity ${
            backDisabled ? 'opacity-50 cursor-not-allowed' : 'hover:text-stone-200'
          }`}
        >
          <span aria-hidden="true">←</span> {backLabel}
        </button>

        {/* Next button */}
        <button
          onClick={handleNext}
          disabled={nextDisabled}
          className={`absolute right-6 top-1/2 -translate-y-1/2 px-6 py-2 rounded bg-cyan-700 text-stone-50 font-medium flex items-center gap-2 transition-colors ${
            nextDisabled ? 'opacity-50 cursor-not-allowed' : 'hover:bg-cyan-800'
          }`}
        >
          {nextLabel} <span aria-hidden="true">→</span>
        </button>
      </div>
    );
  }

  return (
    <div className={`mt-12 sm:mt-14 lg:mt-16 w-full bg-cyan-900 rounded-md shadow-[0_0_16px_4px_rgba(49,49,33,0.20)] relative ${className}`}>
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between p-4 sm:p-5 lg:p-6 gap-4 sm:gap-0">
        {/* Back button - full width on mobile, auto on desktop */}
        <button
          className={`w-full sm:w-auto min-w-[25px] sm:min-w-[30px] py-2 sm:py-1.5 rounded bg-cyan-700 text-center order-2 sm:order-1 transition-opacity ${
            backDisabled ? 'opacity-50 cursor-not-allowed' : 'opacity-90 hover:opacity-100'
          }`}
          onClick={handleBack}
          disabled={backDisabled}
        >
          <span className="flex items-center justify-center gap-2 font-sans text-sm sm:text-base font-medium text-stone-50 leading-none">
            <span aria-hidden="true">←</span>
            <span>{backLabel}</span>
          </span>
        </button>

        {/* Progress section */}
        <div className="flex-1 w-full sm:w-auto min-w-0 order-1 sm:order-2 sm:mx-4">
          <p className="font-sans text-lg sm:text-xl font-medium text-stone-50 leading-relaxed">{progressTitle}</p>
          <p className="font-sans text-xs font-normal text-stone-50/50 leading-none mt-1">{stepText}</p>
          {/* progress bar */}
          <div className="mt-3 w-full max-w-md sm:max-w-lg lg:max-w-2xl h-1.5 bg-stone-400 rounded-md relative">
            <div 
              className="absolute top-0 left-0 h-1.5 bg-green-700 rounded-md transition-all duration-300"
              style={{ width: `${Math.max(0, Math.min(100, progressPercentage))}%` }}
            />
          </div>
        </div>

        {/* Next button - full width on mobile, auto on desktop */}
        <button 
          className={`w-full sm:w-auto min-w-[40px] sm:min-w-[50px] lg:min-w-[55px] py-2 sm:py-1.5 rounded bg-cyan-700 text-center order-3 transition-colors ${
            nextDisabled ? 'opacity-50 cursor-not-allowed' : 'opacity-100 hover:bg-cyan-800'
          }`}
          disabled={nextDisabled}
          onClick={handleNext}
        >
          <span className="flex items-center justify-center gap-2 font-sans text-sm sm:text-base font-medium text-stone-50 leading-none">
            <span>{nextLabel}</span>
            <span aria-hidden="true">→</span>
          </span>
        </button>
      </div>
    </div>
  );
};

export default NavigationFooter; 